import React, { useRef, useState } from 'react';
import { CopyIcon, MailIcon, PhoneIcon, MapPinIcon } from 'lucide-react';
import { CONTACT } from '../../data/navigation';
interface FormState {
  name: string;
  company: string;
  email: string;
  phone: string;
  material: string;
  machine: string;
  horsepower: string;
  workholding: string;
  holderSize: string;
  goal: string;
  quantity: string;
  message: string;
}
const initialState: FormState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  material: '',
  machine: '',
  horsepower: '',
  workholding: '',
  holderSize: '',
  goal: '',
  quantity: '',
  message: ''
};
const requiredFields: {
  key: keyof FormState;
  label: string;
}[] = [{
  key: 'name',
  label: 'Name'
}, {
  key: 'company',
  label: 'Company'
}, {
  key: 'email',
  label: 'Work email'
}, {
  key: 'material',
  label: 'Material'
}, {
  key: 'holderSize',
  label: 'Holder size'
}, {
  key: 'goal',
  label: 'Application goal'
}];
const fieldLabels: Record<keyof FormState, string> = {
  name: 'Name',
  company: 'Company',
  email: 'Work email',
  phone: 'Phone',
  material: 'Material',
  machine: 'Machine model',
  horsepower: 'Machine horsepower',
  workholding: 'Workholding / setup',
  holderSize: 'Holder size',
  goal: 'Application goal',
  quantity: 'Expected quantity',
  message: 'Message'
};
const inputClass = 'mt-2 block w-full min-h-[48px] border border-rule bg-white px-3.5 py-2.5 text-[14.5px] text-graphite transition-colors duration-150 ease-precise placeholder:text-graphite/40 focus:border-copper';
function buildRequestText(values: FormState): string {
  return (Object.keys(fieldLabels) as (keyof FormState)[]).filter(key => values[key].trim().length > 0).map(key => `${fieldLabels[key]}: ${values[key].trim()}`).join('\n');
}
export function ProductGuidance() {
  const [values, setValues] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const summaryRef = useRef<HTMLDivElement>(null);
  function validate(next: FormState) {
    const nextErrors: Partial<Record<keyof FormState, string>> = {};
    requiredFields.forEach(({
      key,
      label
    }) => {
      if (!next[key].trim()) nextErrors[key] = `${label} is required.`;
    });
    if (next.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(next.email)) {
      nextErrors.email = 'Enter a valid work email address.';
    }
    return nextErrors;
  }
  function update(key: keyof FormState, value: string) {
    const next = {
      ...values,
      [key]: value
    };
    setValues(next);
    setCopyState('idle');
    if (submitted) setErrors(validate(next));
  }
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitted(true);
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }
    const subject = `Shear Geometry product guidance — ${values.company.trim()} (${values.material})`;
    const body = buildRequestText(values);
    const href = `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body).slice(0, 1800)}`;
    window.location.href = href;
  }
  async function handleCopy() {
    const text = buildRequestText(values);
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
  }
  const errorList = requiredFields.filter(({
    key
  }) => errors[key]);
  return <section id="product-guidance" aria-labelledby="guidance-heading" className="w-full bg-navy" data-id="element-168" data-mp="31ujybb6etxry">
      <div className="mx-auto grid max-w-shell gap-12 px-5 py-16 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-20 lg:px-10 lg:py-24" data-id="element-169" data-mp="2e3nzhu3je2b7">
        <div data-id="element-170" data-mp="2a11eod5a9hni">
          <p className="flex items-center gap-3 font-mono text-[10px] tracking-label text-copper" data-id="element-171" data-mp="2msd5wy1e9ony">
            <span aria-hidden="true" className="h-px w-8 bg-copper" data-id="element-172" data-mp="2rrfznmbxc8gm" />
            APPLICATION SUPPORT
          </p>
          <h2 id="guidance-heading" className="mt-5 font-display text-[clamp(1.75rem,3.2vw,2.9rem)] font-medium leading-[1.1] text-white" data-id="element-173" data-mp="2wqstws54kht8">
            Tell us what you’re cutting
          </h2>
          <p className="rp-measure mt-6 text-[15.5px] leading-[1.7] text-aluminum" data-id="element-174" data-mp="36faw9dt1ywyz">
            Share the material, machine, horsepower, workholding, holder size,
            target depth of cut and feed, and expected quantity. Robertson
            Precision can help identify the most relevant published starting
            point and product family.
          </p>

          <dl className="mt-10 divide-y divide-white/10 border-y border-white/10" data-id="element-175" data-mp="1l39gdqs2vo4h">
            <div className="flex items-center gap-4 py-4" data-id="element-176" data-mp="1q22spfvmp932">
              <PhoneIcon aria-hidden="true" className="h-4 w-4 text-copper" data-id="element-177" data-mp="1q2m9uudgftg1" />
              <dt className="w-28 font-mono text-[10px] tracking-label text-aluminum/70" data-id="element-178" data-mp="1ijms0t5n69ys">
                CALL
              </dt>
              <dd data-id="element-179" data-mp="1dqjygl0v70il">
                <a href={CONTACT.phoneHref} data-analytics="phone-click" data-analytics-location="product-guidance" className="font-mono text-[15px] text-white underline decoration-copper underline-offset-4 transition-colors duration-150 ease-precise hover:text-cta" data-id="element-180" data-mp="1dq0bur09rny8">
                  {CONTACT.phoneLabel}
                </a>
              </dd>
            </div>
            <div className="flex items-center gap-4 py-4" data-id="element-181" data-mp="1ri89sm3ahkyi">
              <MailIcon aria-hidden="true" className="h-4 w-4 text-copper" data-id="element-182" data-mp="1mk6mfoytyzh8" />
              <dt className="w-28 font-mono text-[10px] tracking-label text-aluminum/70" data-id="element-183" data-mp="1mm9cg8zzgsly">
                EMAIL
              </dt>
              <dd data-id="element-184" data-mp="b59plswen5zb">
                <a href={CONTACT.emailHref} data-analytics="email-click" data-analytics-location="product-guidance" className="break-all text-[15px] text-white underline decoration-copper underline-offset-4 transition-colors duration-150 ease-precise hover:text-cta" data-id="element-185" data-mp="b4qccphp95rm">
                  {CONTACT.email}
                </a>
              </dd>
            </div>
            <div className="flex items-start gap-4 py-4" data-id="element-186" data-mp="2udyj9cw8gaz5">
              <MapPinIcon aria-hidden="true" className="mt-0.5 h-4 w-4 text-copper" data-id="element-187" data-mp="2flwmi1pgly3c" />
              <dt className="w-28 shrink-0 font-mono text-[10px] leading-relaxed tracking-label text-aluminum/70" data-id="element-188" data-mp="2kezc5yr48ne9">
                MAILING ADDRESS
              </dt>
              <dd className="text-[14.5px] leading-relaxed text-aluminum" data-id="element-189" data-mp="2bmfhf9dy32yp">
                {CONTACT.mailingAddress}
              </dd>
            </div>
          </dl>
        </div>

        <div className="border border-white/10 bg-white p-6 sm:p-9" data-id="element-190" data-mp="21npx79ykt6fa">
          <form onSubmit={handleSubmit} noValidate data-id="element-191" data-mp="21p14vkv56ybo">
            {errorList.length > 0 ? <div ref={summaryRef} tabIndex={-1} role="alert" className="mb-7 border-l-2 border-cta bg-warm p-4" data-id="element-192" data-mp="1i37veq656jdw">
                <p className="text-[14px] font-semibold text-navy" data-id="element-193" data-mp="1rxhrahualztd">
                  Please complete {errorList.length} required{' '}
                  {errorList.length === 1 ? 'field' : 'fields'} before
                  continuing.
                </p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-[13.5px] text-graphite/85" data-id="element-194" data-mp="1wxm8ka30yykn">
                  {errorList.map(({
                key,
                label
              }) => <li key={key} data-id="element-195" data-mp="2arwrgascbyis">
                      <a href={`#field-${key}`} className="underline underline-offset-2" data-id="element-196" data-mp="25qphc52q6n8y">
                        {label}
                      </a>
                      : {errors[key]}
                    </li>)}
                </ul>
              </div> : null}

            <p className="font-mono text-[10px] tracking-label text-copper" data-id="element-197" data-mp="10f6uso1fq9uo">
              REQUEST FORM · REQUIRED FIELDS MARKED (REQUIRED)
            </p>

            <div className="mt-6 grid gap-5 sm:grid-cols-2" data-id="element-198" data-mp="1acbkgxuhong8">
              <Field id="name" label="Name" required value={values.name} error={errors.name} onChange={v => update('name', v)} autoComplete="name" data-id="element-199" data-mp="1f9toqaxkaehw" />
              <Field id="company" label="Company" required value={values.company} error={errors.company} onChange={v => update('company', v)} autoComplete="organization" data-id="element-200" data-mp="mj34v6xlgno0" />
              <Field id="email" label="Work email" type="email" required value={values.email} error={errors.email} onChange={v => update('email', v)} autoComplete="email" data-id="element-201" data-mp="3uzeql7wkfnp4" />
              <Field id="phone" label="Phone" type="tel" value={values.phone} onChange={v => update('phone', v)} autoComplete="tel" data-id="element-202" data-mp="2849n18cdmcg5" />

              <SelectField id="material" label="Material" required value={values.material} error={errors.material} onChange={v => update('material', v)} options={['Aluminum / 6061-T6', 'Copper', 'Soft aluminum alloy', 'Titanium', 'Other']} data-id="element-203" data-mp="1y7wtcagtzyle" />
              <SelectField id="holderSize" label="Holder size" required value={values.holderSize} error={errors.holderSize} onChange={v => update('holderSize', v)} options={['1 inch', '3/4 inch', 'Not sure']} data-id="element-204" data-mp="1ahspc1zqdwtv" />

              <Field id="machine" label="Machine model" value={values.machine} onChange={v => update('machine', v)} data-id="element-205" data-mp="16f11uxywfm9l" />
              <Field id="horsepower" label="Machine horsepower" value={values.horsepower} onChange={v => update('horsepower', v)} data-id="element-206" data-mp="enuz09rsnagm" />
              <Field id="workholding" label="Workholding / setup" value={values.workholding} onChange={v => update('workholding', v)} data-id="element-207" data-mp="nk2mhw2l5d9u" />
              <Field id="quantity" label="Expected quantity" value={values.quantity} onChange={v => update('quantity', v)} data-id="element-208" data-mp="1u2rcd20ywjvo" />

              <div className="sm:col-span-2" data-id="element-209" data-mp="26lmo29nrkeer">
                <Field id="goal" label="Application goal" required value={values.goal} error={errors.goal} onChange={v => update('goal', v)} data-id="element-210" data-mp="26mo996yhuha7" />
              </div>
              <div className="sm:col-span-2" data-id="element-211" data-mp="2lbc2v7os4gxi">
                <label htmlFor="field-message" className="block text-[13px] font-semibold text-navy" data-id="element-212" data-mp="2q945avn1vlfy">
                  Message
                </label>
                <textarea id="field-message" name="message" rows={4} value={values.message} onChange={e => update('message', e.target.value)} className={inputClass} data-id="element-213" data-mp="2gj14wadln57l" />
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-214" data-mp="3iuj14awaru9n">
              <button type="submit" data-analytics="product-guidance-cta" data-analytics-location="form-submit" className="inline-flex min-h-[52px] items-center justify-center bg-cta px-7 text-[14.5px] font-semibold text-navy transition-colors duration-150 ease-precise hover:bg-navy hover:text-white" data-id="element-215" data-mp="3dvzh4g19ksjx">
                Request Product Guidance
              </button>
              <button type="button" onClick={handleCopy} className="inline-flex min-h-[52px] items-center justify-center gap-2 border border-rule px-6 text-[14px] font-medium text-navy transition-colors duration-150 ease-precise hover:border-copper hover:text-copper" data-id="element-216" data-mp="2v4ors3b6snu5">
                <CopyIcon aria-hidden="true" className="h-4 w-4" data-id="element-217" data-mp="2c75h6v92yx5m" />
                Copy Request
              </button>
            </div>

            <p aria-live="polite" className="mt-3 text-[13px] text-graphite/80" data-id="element-218" data-mp="ro1mzphlfvfz">
              {copyState === 'copied' ? 'Request copied to the clipboard.' : copyState === 'failed' ? 'Copy failed. Select the text of your entries, or email the details directly.' : ''}
            </p>

            <p className="mt-3 text-[13px] leading-relaxed text-graphite/75" data-id="element-219" data-mp="15mi0bz9yok73">
              Submitting opens your email app so you can review and send the
              request.
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-graphite/75" data-id="element-220" data-mp="cxuarri6j8nq">
              No email app configured? Send the details to{' '}
              <a href={CONTACT.emailHref} data-analytics="email-click" data-analytics-location="form-fallback" className="font-medium text-navy underline decoration-copper underline-offset-2" data-id="element-221" data-mp="mq9xf24f8ley">
                {CONTACT.email}
              </a>{' '}
              or call{' '}
              <a href={CONTACT.phoneHref} data-analytics="phone-click" data-analytics-location="form-fallback" className="font-medium text-navy underline decoration-copper underline-offset-2" data-id="element-222" data-mp="1wu03g6znswxr">
                {CONTACT.phoneLabel}
              </a>
              .
            </p>
          </form>
        </div>
      </div>
    </section>;
}
interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  error?: string;
  autoComplete?: string;
}
function Field({
  id,
  label,
  value,
  onChange,
  required = false,
  type = 'text',
  error,
  autoComplete
}: FieldProps) {
  const fieldId = `field-${id}`;
  const errorId = `${fieldId}-error`;
  return <div data-id="element-223" data-mp="q7xmwq1z75kb">
      <label htmlFor={fieldId} className="block text-[13px] font-semibold text-navy" data-id="element-224" data-mp="q8fp03bbke05">
        {label}
        {required ? <span className="ml-1.5 font-mono text-[10px] font-normal tracking-label text-copper" data-id="element-225" data-mp="g4sjslzx66jr">
            (REQUIRED)
          </span> : null}
      </label>
      <input id={fieldId} name={id} type={type} value={value} autoComplete={autoComplete} aria-required={required} aria-invalid={error ? true : undefined} aria-describedby={error ? errorId : undefined} onChange={e => onChange(e.target.value)} className={`${inputClass} ${error ? 'border-cta' : ''}`} data-id="element-226" data-mp="6ez5vecb1fcc" />
      {error ? <p id={errorId} className="mt-1.5 text-[12.5px] font-medium text-navy" data-id="element-227" data-mp="3jlr5t1mx850v">
          {error}
        </p> : null}
    </div>;
}
interface SelectFieldProps extends Omit<FieldProps, 'type'> {
  options: string[];
}
function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  required = false,
  error
}: SelectFieldProps) {
  const fieldId = `field-${id}`;
  const errorId = `${fieldId}-error`;
  return <div data-id="element-228" data-mp="1kf3smhlwf67b">
      <label htmlFor={fieldId} className="block text-[13px] font-semibold text-navy" data-id="element-229" data-mp="1kbocrckeht4u">
        {label}
        {required ? <span className="ml-1.5 font-mono text-[10px] font-normal tracking-label text-copper" data-id="element-230" data-mp="1be5l3pc94gjz">
            (REQUIRED)
          </span> : null}
      </label>
      <select id={fieldId} name={id} value={value} aria-required={required} aria-invalid={error ? true : undefined} aria-describedby={error ? errorId : undefined} onChange={e => onChange(e.target.value)} className={`${inputClass} ${error ? 'border-cta' : ''}`} data-id="element-231" data-mp="241rx9lzjotig">
        <option value="" data-id="element-232" data-mp="1pbu7x2pvpxro">Select…</option>
        {options.map(option => <option key={option} value={option} data-id="element-233" data-mp="2msrj4ebqtn7i">
            {option}
          </option>)}
      </select>
      {error ? <p id={errorId} className="mt-1.5 text-[12.5px] font-medium text-navy" data-id="element-234" data-mp="2i0rdq6cwm23j">
          {error}
        </p> : null}
    </div>;
}